import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { changePassword } from 'redux/slices/auth/authSlice';
import { AppDispatch } from 'redux/store';
import { logoutUser } from 'redux/slices/user/userSlice';
import TextInput from 'components/Common/TextInput';
import { notify } from 'components/Common/Toast';
import AuthLayaout from 'pages/Auth/AuthLayout';
import Routes from 'constants/routes';
import CustomButton from 'components/Common/CustomButton';
import CardWrapper from 'components/Common/CardWrapper';
import MuiLoader from 'components/Common/Loader';
import { PASSWORD_SCHEMA } from 'utils/helpers';

const formSchema = yup
    .object({
        currentPassword: yup.string().required('Enter your current password'),
        newPassword: PASSWORD_SCHEMA.label('New Password'),
        confirmPassword: PASSWORD_SCHEMA.label('Confirm Password').oneOf([yup.ref('newPassword')], 'Your passwords do not match.'),
    })
    .required();

export interface ChangePasswordFormInputs {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export default function MyAccountChangePassword({ type }: { type?: string }) {
    const navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    let description = `Your new password must be different from previous used passwords and must be of at least 8 characters.`;
    if (type === 'resetPassword') {
        description = `Please create your permanent password that you will use to login to this account.`;
    }
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ChangePasswordFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
    });
    const handleLogout = async () => {
        await dispatch(logoutUser());
        navigation(Routes.auth.login);
    };
    const submit = async (data: { [x: string]: any }) => {
        const { currentPassword, newPassword } = data;
        setIsLoading(true);
        try {
            const result: any = await dispatch(changePassword({ currentPassword, newPassword }));
            const { contextResponse } = result?.payload || {};
            if (contextResponse?.statusCode) {
                notify('Password Changed Successfully!', 'success');
                if (type) {
                    navigation(Routes.dashboard.main);
                } else {
                    handleLogout();
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const content = () => {
        return (
            <div className="Auth-form">
                {type !== 'resetPassword' && (
                    <Box>
                        <Tooltip title="Logout">
                            <IconButton
                                disabled={isLoading}
                                onClick={handleLogout}
                                sx={{ position: 'absolute', top: '10px', right: '-10px' }}
                            >
                                {isLoading ? <MuiLoader size={20} loading={isLoading} /> : <LogoutIcon />}
                            </IconButton>
                        </Tooltip>
                        <p className="Auth-form-title">Create new password</p>
                    </Box>
                )}
                <Box>
                    <Controller
                        name="currentPassword"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                sx={{ mt: 2, mb: 3 }}
                                type="password"
                                width={'100%'}
                                error={!!errors?.currentPassword}
                                helperText={(errors.currentPassword as any)?.message}
                                label="Current Password"
                            />
                        )}
                    />
                    <Controller
                        name="newPassword"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                sx={{ mt: 2, mb: 4 }}
                                width={'100%'}
                                type="password"
                                error={!!errors?.newPassword}
                                helperText={(errors.newPassword as any)?.message}
                                label="Password"
                                fieldDescription="Must be 8-20 characters long"
                            />
                        )}
                    />
                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                width={'100%'}
                                sx={{ mt: 2, mb: 2 }}
                                type="password"
                                error={!!errors?.confirmPassword}
                                helperText={(errors.confirmPassword as any)?.message}
                                fieldDescription="Both passwords must match."
                                label="Confirm password"
                            />
                        )}
                    />
                    <CustomButton
                        loading={isLoading}
                        title={type === 'resetPassword' ? 'Save' : 'Confirm'}
                        onClick={handleSubmit(submit)}
                        sx={{ fontSize: { xs: 14, sm: 18 }, mt: 5 }}
                        width={'100%'}
                    />
                </Box>
            </div>
        );
    };
    if (type === 'resetPassword') {
        return (
            <CardWrapper>
                <Stack direction={'column'}>
                    <p className="Auth-form-title">Change password</p>
                    <Divider />
                    <Typography>
                        Your new password must be different from previously used passwords and must be at least 8 characters long,
                        contain...
                    </Typography>
                </Stack>
                <Stack width={{ lg: '100%' }} alignItems="center">
                    {content()}
                </Stack>
            </CardWrapper>
        );
    }
    return <AuthLayaout>{content()}</AuthLayaout>;
}
